/**
 * Utility functions for filtering and processing credit card benefits with tangible monetary value
 */

import { AdvancedCategory, FrequencyLike } from 'data/types';

export interface CreditBenefit {
  name: string;
  description: string;
  benefit_type?: string | AdvancedCategory | null;
  new_benefit_type?: string | null;
  spend_requirement?: number | null;
  reward_frequency?: FrequencyLike | null;
  applies_first_year?: boolean | null;
  requires_authorized_user?: boolean | null;
}

/**
 * Benefit types that represent tangible monetary value (credits, free nights, memberships, etc.)
 * These are stable enum values that won't change with text processing
 */
const TANGIBLE_BENEFIT_TYPES = [
  'travel_credit',
  'statement_credit',
  'dining_credit',
  'entertainment_credit',
  'shopping_credit',
  'uber_credit',
  'airline_credit',
  'hotel_credit',
  'resort_credit',
  'annual_credit',
  'monthly_credit',
  'quarterly_credit',
  'free_hotel_night',
  'airfare_discount',
  'free_doordash_dashpass',
  'bonus_points',
  'bank_of_america_preferred_rewards',
  'anniversary_bonus',
  'tsa_pre_and_global_entry_credit',
] as const;

const TANGIBLE_BENEFIT_TYPES_SET = new Set<string>(TANGIBLE_BENEFIT_TYPES);

const isTangibleBenefitType = (value: string): boolean =>
  TANGIBLE_BENEFIT_TYPES_SET.has(value);

/**
 * Filters benefits to only include those with tangible monetary value
 * Uses stable enum values to avoid text processing issues
 */
export function filterTangibleBenefits(
  benefits: CreditBenefit[] | null | undefined
): CreditBenefit[] {
  if (!benefits || !Array.isArray(benefits)) {
    return [];
  }

  return benefits.filter((benefit) => {
    const benefitType =
      typeof benefit.benefit_type === 'string'
        ? benefit.benefit_type
        : benefit.new_benefit_type;
    return typeof benefitType === 'string' && isTangibleBenefitType(benefitType);
  });
}

/**
 * Checks if a benefit has tangible monetary value
 */
export function isTangibleBenefit(benefit: CreditBenefit): boolean {
  const benefitType =
    typeof benefit.benefit_type === 'string'
      ? benefit.benefit_type
      : benefit.new_benefit_type;
  return (
    !!benefitType &&
    typeof benefitType === 'string' &&
    isTangibleBenefitType(benefitType)
  );
}

/**
 * Extracts monetary value from benefit name or description
 * Returns the first dollar amount found, or null if none found
 */
export function extractMonetaryValue(benefit: CreditBenefit): number | null {
  const name = benefit.name || '';
  const description = benefit.description || '';

  // Look for dollar amounts in the name or description
  const amountMatch = `${name} ${description}`.match(
    /\$(\d+(?:,\d{3})*(?:\.\d{2})?)/
  );

  return amountMatch ? parseFloat(amountMatch[1].replace(/,/g, '')) : null;
}

/**
 * Formats frequency text for display
 */
export function formatFrequencyText(
  frequency?: FrequencyLike | null
): string {
  if (!frequency) {
    return '';
  }

  const normalized = String(frequency).toLowerCase();

  switch (normalized) {
    case 'annually':
      return '/year';
    case 'monthly':
      return '/month';
    case 'quarterly':
      return '/quarter';
    case 'biannually':
      return '/6 months';
    default:
      return '';
  }
}

/**
 * Gets a user-friendly label for the benefit type
 */
export function getBenefitTypeLabel(benefitType: string): string {
  const labels: Record<string, string> = {
    travel_credit: 'Travel Credit',
    statement_credit: 'Statement Credit',
    dining_credit: 'Dining Credit',
    entertainment_credit: 'Entertainment Credit',
    shopping_credit: 'Shopping Credit',
    uber_credit: 'Uber Credit',
    airline_credit: 'Airline Credit',
    hotel_credit: 'Hotel Credit',
    resort_credit: 'Resort Credit',
    annual_credit: 'Annual Credit',
    monthly_credit: 'Monthly Credit',
    quarterly_credit: 'Quarterly Credit',
    free_hotel_night: 'Free Hotel Night',
    airfare_discount: 'Airfare Discount',
    free_doordash_dashpass: 'DashPass Membership',
    bonus_points: 'Bonus Points',
    bank_of_america_preferred_rewards: 'Preferred Rewards Bonus',
    anniversary_bonus: 'Anniversary Bonus',
    tsa_pre_and_global_entry_credit: 'TSA PreCheck & Global Entry Credit',
  };

  return (
    labels[benefitType] ||
    benefitType.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
  );
}
